import React, { useEffect, useMemo, useState } from 'react';
import { ProfessionalCard } from './components/ProfessionalCard';
import { CalendarSchedule } from '../../../../pages/DateTime/components';
import Accordion from '../../../../components/Accordion';
import { ProductItemProps } from '../../../../models/Product';
import { fetchDateSlots, fetchSlotsByDate, SlotHour } from '../../../../modules/slots';
import { TimePicker } from '../TimePicker';
import { Button } from '../../../../components';
import Loading from '../../../../components/Loading';
import { addDays, differenceInDays, differenceInMinutes, isSameDay } from 'date-fns';
import { daysInMonth } from '../../../../utils/dates';
import DatePickerModal from '../DatePickerModal';
import { useRemoteConfig } from '../../../../hooks/useRemoteConfig';
import { envConfig } from '../../../../utils/enviroment';

export type DisplayTimes = {
  id: string;
  time: Date;
  slots: SlotHour[];
};

export type ScheduleDate = {
  scheduleId: string;
  date?: Date;
};

export type ScheduleDisplayTime = {
  scheduleId: string;
  displayTime?: DisplayTimes;
};

export type ProductSlotsProps = {
  product: ProductItemProps;
  fixed?: boolean;
  exclude?: { date: string; hour: string }[];
  check?: boolean;
  onSubmit?: (slot: SlotHour) => void;
  onValueChange?: (slot?: SlotHour) => void;
  onError?: (error: Error) => void;
  showScheduleButton?: boolean;
  visible?: boolean;
  toggleVisible: () => void;
  hour?: SlotHour;
  handleWithoutSlots: () => void;
  firstAppointmentDate: string;
  isFirstAppointment: boolean;
};

export function ProductSlots({
  product,
  fixed,
  exclude,
  check,
  onSubmit,
  onValueChange,
  onError,
  showScheduleButton,
  visible,
  toggleVisible,
  hour,
  handleWithoutSlots,
  firstAppointmentDate,
  isFirstAppointment
}: ProductSlotsProps) {
  const [initialized, setInitialized] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dates, setDates] = useState<string[]>([]);
  const [hours, setHours] = useState<SlotHour[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedHour, setSelectedHour] = useState<SlotHour | undefined>(hour);
  const [datesLoading, setDatesLoading] = useState(false);
  const [hoursLoading, setHoursLoading] = useState(false);
  const [displayDates, setDisplayDates] = React.useState<Date[]>([]);

  const { getRemoteConfigValue } = useRemoteConfig();

  useEffect(() => {
    onValueChange?.(selectedHour);
  }, [selectedHour]);

  const validateReturnsInterval = (): number => {
    if (firstAppointmentDate) {
      const firstAppointment = new Date(firstAppointmentDate);
      return differenceInDays(new Date(), firstAppointment);
    }
    return 0;
  };

  const dateOfFirstAppointment = () => {
    const differenceDays = validateReturnsInterval();

    const INTERVAL_RETURN_DAYS = Number(
      getRemoteConfigValue('checkup_digital_return_days') ?? (envConfig.REACT_APP_DAYS_TO_ALLOW_RETURNS || 0)
    );

    if (differenceDays < INTERVAL_RETURN_DAYS && !isFirstAppointment)
      return addDays(new Date(), INTERVAL_RETURN_DAYS - differenceDays + 1);
    return addDays(new Date(), 1);
  };

  const getDates = async (baseDate: Date) => {
    setDatesLoading(true);

    let startDate = addDays(new Date(), 1);

    const firstAppointmentDate = dateOfFirstAppointment();

    if (firstAppointmentDate) {
      const firstAvailableDate = new Date(firstAppointmentDate);
      if (baseDate <= firstAvailableDate) startDate = firstAvailableDate;
    }

    if (baseDate.getMonth() > startDate.getMonth() || baseDate.getFullYear() > startDate.getFullYear()) {
      startDate.setDate(1);
      startDate.setMonth(baseDate.getMonth());
      startDate.setFullYear(baseDate.getFullYear());
    }
    const daysOnMonth = daysInMonth(baseDate.getMonth() + 1, baseDate.getFullYear());
    const endDate = new Date(startDate.getFullYear(), startDate.getMonth(), daysOnMonth);

    if (differenceInDays(endDate, startDate) < 4) endDate.setDate(endDate.getDate() + 5);

    const [coverage] = product.coverages || [];

    try {
      const response = await fetchDateSlots({
        startDate: startDate.toISOString().substring(0, 10),
        endDate: endDate.toISOString().substring(0, 10),
        isKit: coverage.is_kit,
        itemId: coverage.item_SGH_id,
        unitId: coverage.units?.[0]?.id_sgh ?? '',
        localId: coverage.units?.[0]?.local_id || coverage.units?.[0]?.locations?.[0]?.name || ''
      });

      setDates(response.slotDates);

      if (!response.slotDates.length) {
        handleWithoutSlots();
      }

      if (response.slotDates.length > 0 && !initialized) {
        setSelectedDate(new Date(response.slotDates[0] + 'T00:00:00'));
        setInitialized(true);
        setDisplayDates(mapDates(response.slotDates).slice(0, 3));
      }
    } catch (error: any) {
      handleWithoutSlots();
      console.log('error', error);
      onError?.(error);
    } finally {
      setDatesLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      getDates(new Date());
    }
  }, [visible]);

  const isDateGreaterOrEqual24Hours = (date: Date): boolean => date > addDays(new Date(), 1);

  const excludeHoursLess24Hours = (hours: SlotHour[]): SlotHour[] =>
    hours.filter(hour => isDateGreaterOrEqual24Hours(new Date(`${hour.date}T${hour.hour}`)));

  const getHours = async () => {
    if (!selectedDate) return;
    setHours([]);

    setHoursLoading(true);

    const [coverage] = product.coverages || [];

    const response = await fetchSlotsByDate({
      isKit: coverage.is_kit,
      itemId: coverage.item_SGH_id,
      unitId: coverage.units?.[0]?.id_sgh ?? '',
      date: selectedDate.toISOString().substring(0, 10),
      localId: coverage.units?.[0]?.local_id || coverage.units?.[0]?.locations?.[0]?.name || ''
    });

    const datesGreater24Hours = excludeHoursLess24Hours(response);

    setHours(datesGreater24Hours);
    setSelectedHour(undefined);
    setHoursLoading(false);
  };

  useEffect(() => {
    if (selectedDate) {
      getHours();
    }
  }, [selectedDate]);

  const mapDates = (dates: string[]): Date[] => {
    return dates.map(date => new Date(date + 'T00:00:00'));
  };

  const excludedSet = useMemo(() => {
    const set = new Set<string>();
    exclude?.forEach(({ date, hour }) => set.add(`${date}T${hour}`));
    return set;
  }, [exclude]);

  const excludeHours = (hours: SlotHour[]): SlotHour[] => {
    const APPOINTMENTS_INTERVAL = 45;

    return hours.filter(hour => {
      const dateCheck = new Date(`${hour.date}T${hour.hour}`);

      let isDateInRange = false;

      excludedSet.forEach(item => {
        const dateToFilter = new Date(item);

        if (isSameDay(dateToFilter, dateCheck)) {
          const difference = differenceInMinutes(dateToFilter, dateCheck);

          if (difference >= -APPOINTMENTS_INTERVAL && difference <= APPOINTMENTS_INTERVAL) isDateInRange = true;
        }
      });

      if (isDateInRange) return false;
      return true;
    });
  };

  return (
    <Accordion
      key={product.id}
      handleOpen={toggleVisible}
      isOpen={!!visible}
      fixed={fixed}
      externalContent={
        <ProfessionalCard
          date={selectedHour ? new Date(selectedHour.date + 'T' + selectedHour.hour) : undefined}
          productName={product.name || ''}
          slotResourceID={selectedHour?.resourceId || String(!!visible)}
          professionalName={selectedHour?.resource || ''}
          showCheck={check}
        />
      }
    >
      {datesLoading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loading loading={datesLoading} size={60} color={'#004f92'} />
        </div>
      )}

      {!datesLoading && dates.length > 1 && (
        <CalendarSchedule
          // key={schedule.id}
          displayDates={displayDates}
          selectedDate={selectedDate}
          onSelectDate={(date: Date) => setSelectedDate(date)}
          handleClickSeeMore={() => setIsDialogOpen(true)}
        />
      )}

      {!datesLoading && dates.length === 0 && <div>Sem horários disponíveis</div>}

      {!hoursLoading && !datesLoading ? (
        <TimePicker slots={excludeHours(hours)} value={selectedHour} onValueChange={setSelectedHour} />
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loading loading={hoursLoading} size={60} color={'#004f92'} />
        </div>
      )}

      {hours.length > 1 && showScheduleButton && (
        <Button title={'Agendar'} disabled={!selectedHour} onClick={() => selectedHour && onSubmit?.(selectedHour)} />
      )}

      <DatePickerModal
        selectedDate={selectedDate}
        setSelectedDate={date => date && setSelectedDate(date)}
        displayDates={displayDates}
        setDisplayDates={setDisplayDates}
        isOpen={isDialogOpen}
        setIsOpen={setIsDialogOpen}
        displayDatesPicker={mapDates(dates)}
        onMonthChange={getDates}
        loading={datesLoading}
      />
    </Accordion>
  );
}
